import type { FilterRuleModel } from '@/shared/generated'
import type { TFunction } from 'i18next'

import React from 'react'
import { any, groupBy, map, pipe, prop, uniqBy } from 'lodash/fp'
import { DownloadIcon, RotateCcwIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import {
	getAssociatedStatus,
	getOptimizationsMap,
	useBulkOptimizationMutation,
	useOptimizationsQuery,
} from '@/entities/optimization'
import { useProductsCountsByPcQuery } from '@/entities/pricing-campaign'

import { Button } from '@/shared/components/ui/button'
import { clarify } from '@/shared/components/ui/clarify'
import { confirm } from '@/shared/components/ui/confirmer'
import {
	Item,
	ItemActions,
	ItemContent,
	ItemDescription,
	ItemTitle,
} from '@/shared/components/ui/item'
import { Spinner } from '@/shared/components/ui/spinner'
import { OptimizationAction, OptimizationStatus } from '@/shared/generated'
import { ampli } from '@/shared/lib/ampli'
import { cn } from '@/shared/lib/utils'
import { errorMessage } from '@/shared/tools/message'

type OptimizationsByStatus = Record<OptimizationStatus, Optimizations>
type OptimizationsActionsProps = {
	rules: FilterRuleModel[]
	onApply: (rules: FilterRuleModel[]) => void
	close: () => void
	handleExport: () => void
}
export const OptimizationsActions = ({
	rules,
	close,
	onApply,
	handleExport,
}: OptimizationsActionsProps) => {
	const { t } = useTranslation()

	const optimizations = useOptimizationsQuery({
		select: getOptimizationsMap,
	})
	const optimizationsByStatus =
		useProductsCountsByPcQuery<OptimizationsByStatus>(
			{
				filters: rules,
			},
			{
				enabled: !!optimizations.data,
				select: pipe([
					uniqBy('optimization_group_id'),
					map(
						({ optimization_group_id }: { optimization_group_id: string }) => {
							const optimization = optimizations?.data?.[optimization_group_id!]

							return {
								optimization_group_id: optimization_group_id!,
								optimization_status:
									optimization?.status === OptimizationStatus.Updating
										? optimization.status
										: getAssociatedStatus(
												optimization?.status as OptimizationStatus
											),
								optimization_id: optimization?.id,
								optimization_is_exported: optimization?.is_exported,
							}
						}
					),
					groupBy('optimization_status'),
				]),
			}
		)
	const bulkAction = useBulkOptimizationMutation()
	const handleOptimizationBulkAction =
		(items: Optimizations) =>
		async ({
			action,
			analyticEvent,
		}: {
			action: OptimizationAction
			analyticEvent: string
		}) => {
			try {
				if (
					action === OptimizationAction.Lock &&
					hasExportedOptimization(items)
				) {
					const answer = await confirm({
						title: t('optimization_export_again_title'),
						description: t('optimization_export_again_message'),
						actionText: t('general_apply'),
						cancelText: t('general_cancel'),
					})
					if (!answer) return
				}
				if (action === OptimizationAction.Unlock) {
					const answer = await clarify({
						title: t('opt.confirm.revert_applied.title'),
						description: t('opt.confirm.revert_applied.description'),
						actions: [
							{
								actionId: 'keep_optimized_prices',
								ActionProps: { variant: 'secondary' },
								actionText: t(
									'opt.confirm.revert_applied.action.keep_optimized_prices'
								),
							},
							{
								actionId: 'revert_full_state',
								ActionProps: { variant: 'primary' },
								actionText: t(
									'opt.confirm.revert_applied.action.revert_full_state'
								),
							},
						],
					})
					if (!answer || answer === 'close') return
					if (answer === 'revert_full_state') {
						await bulkAction.mutateAsync(
							items.map(({ optimization_id, optimization_group_id }) => ({
								optimization_group_id,
								optimization_id,
								action: OptimizationAction.DeleteExport,
							}))
						)
					}
				}
				trackAnalyticEvent(analyticEvent)
				await bulkAction.mutateAsync(
					items.map(({ optimization_id, optimization_group_id }) => ({
						optimization_group_id,
						action,
					}))
				)
			} catch (err) {
				toast.error(errorMessage(t), {
					duration: 3000,
				})
			}
		}
	const isLoading = any(prop('isLoading'), [
		optimizations,
		optimizationsByStatus,
	])
	const isPending = bulkAction.isPending
	const handleApplyFilter = (ids: string[]) => {
		if (!ids.length) return
		onApply([
			...rules.filter(({ name }) => name !== 'optimization_group_id'),
			{
				name: 'optimization_group_id',
				operation: 'in',
				value: ids,
			},
		])
		close()
	}

	return (
		<div className='space-y-2 p-2'>
			<p className='text-sm font-normal text-muted-foreground'>
				{t('optimization_actions_dropdown.title', {
					defaultValue: 'Update OG’s statuses for selected products',
				})}
			</p>
			{actions({ isDisabled: optimizationsByStatus.isLoading }, t).map(
				({ key, getActionButtons, className }) => {
					const groupItems = optimizationsByStatus.data?.[key]

					const totalOGs = groupItems?.length

					return (
						<Item
							size='sm'
							variant='outline'
							key={key}
							className={cn(
								totalOGs ? className : 'bg-accent',
								'flex w-full items-center justify-between rounded-sm p-2'
							)}
						>
							<ItemContent
								className={cn('w-full', {
									'cursor-pointer': totalOGs,
								})}
								typeof='button'
								onClick={() => handleApplyFilter(getOgIds(groupItems))}
							>
								{(isLoading || isPending) && <Spinner />}
								{optimizationsByStatus.data && !isLoading && !isPending && (
									<ItemTitle>{totalOGs || 0}</ItemTitle>
								)}
								<ItemDescription>
									{t(`og_status_${key}`, { defaultValue: key })}
								</ItemDescription>
							</ItemContent>
							{totalOGs && getActionButtons && (
								<ItemActions className='flex items-center justify-between gap-1'>
									{getActionButtons({
										handleClick: handleOptimizationBulkAction(groupItems!),
										handleExport,
									})}
								</ItemActions>
							)}
						</Item>
					)
				}
			)}
		</div>
	)
}
const actions = ({ isDisabled }: { isDisabled: boolean }, t: TFunction) => [
	{
		key: OptimizationStatus.New,
		className: 'bg-secondary hover:bg-tertiary data-[active=true]:bg-tertiary',
		getActionButtons: ({ handleClick }: ActionButtonsProps) => (
			<Button
				size='sm'
				disabled={isDisabled}
				onClick={() =>
					handleClick({
						action: OptimizationAction.Start,
						analyticEvent: getAnalyticEventName(),
					})
				}
			>
				{t('optimization_groups_status_btn_start')}
			</Button>
		),
	},
	{
		key: OptimizationStatus.Updating,
		className: 'bg-orange-50 dark:bg-orange-950',
	},
	{
		key: OptimizationStatus.Pending,
		className: 'bg-orange-50 dark:bg-orange-950',
		getActionButtons: ({ handleClick }: ActionButtonsProps) => (
			<Button
				size='sm'
				disabled={isDisabled}
				onClick={() =>
					handleClick({
						action: OptimizationAction.Update,
						analyticEvent: getAnalyticEventName(),
					})
				}
			>
				{t('app.update')}
			</Button>
		),
	},
	{
		key: OptimizationStatus.Scheduled,
		className: 'bg-orange-50 dark:bg-orange-950',
		getActionButtons: ({ handleClick }: ActionButtonsProps) => (
			<Button
				size='sm'
				disabled={isDisabled}
				onClick={() =>
					handleClick({
						action: OptimizationAction.Stop,
						analyticEvent: getAnalyticEventName(),
					})
				}
			>
				{t('general_cancel')}
			</Button>
		),
	},
	{
		key: OptimizationStatus.Failed,
		className: 'bg-red-50 dark:bg-red-950',
		getActionButtons: ({ handleClick }: ActionButtonsProps) => (
			<Button
				size='sm'
				disabled={isDisabled}
				onClick={() =>
					handleClick({
						action: OptimizationAction.Start,
						analyticEvent: getAnalyticEventName(OptimizationStatus.Failed),
					})
				}
			>
				{t('optimization_groups_status_btn_restart')}
			</Button>
		),
	},
	{
		key: OptimizationStatus.Finished,
		className: 'bg-green-50 dark:bg-green-950',
		getActionButtons: ({ handleClick }: ActionButtonsProps) => (
			<>
				<Button
					size='icon'
					disabled={isDisabled}
					onClick={() =>
						handleClick({
							action: OptimizationAction.Reset,
							analyticEvent: getAnalyticEventName(OptimizationAction.Reset),
						})
					}
				>
					<RotateCcwIcon />
				</Button>
				<Button
					disabled={isDisabled}
					onClick={() =>
						handleClick({
							action: OptimizationAction.Lock,
							analyticEvent: getAnalyticEventName(OptimizationAction.Lock),
						})
					}
				>
					{t('general_apply')}
				</Button>
			</>
		),
	},
	{
		key: OptimizationStatus.Locked,
		className: 'bg-primary-50 dark:bg-primary-950',
		getActionButtons: ({ handleClick, handleExport }: ActionButtonsProps) => (
			<>
				<Button
					size='icon'
					disabled={isDisabled}
					onClick={() =>
						handleClick({
							action: OptimizationAction.Unlock,
							analyticEvent: getAnalyticEventName(OptimizationAction.Unlock),
						})
					}
				>
					<RotateCcwIcon />
				</Button>
				<Button disabled={isDisabled} onClick={handleExport}>
					<DownloadIcon /> {t('status_panel_modal_download')}
				</Button>
			</>
		),
	},
]
const hasExportedOptimization = any(prop('optimization_is_exported'))
const getOgIds = (groups: Optimizations = []) =>
	groups.map(({ optimization_group_id }) => optimization_group_id) || []
const trackAnalyticEvent = (eventName: string) => {
	switch (eventName) {
		case 'mass repricing: action: restart':
			ampli.massRepricingActionRestart()
			break
		case 'mass repricing: action: apply':
			ampli.massRepricingActionApply()
			break
		case 'mass repricing: action: discard':
			ampli.massRepricingActionDiscard()
			break
		case 'mass repricing: action: revert':
			ampli.massRepricingActionRevert()
			break
		default:
			ampli.massRepricingActionStart()
	}
}
const getAnalyticEventName = (status?: string) => {
	switch (status) {
		case OptimizationStatus.Failed:
			return 'mass repricing: action: restart'
		case OptimizationAction.Lock:
			return 'mass repricing: action: apply'
		case OptimizationAction.Unlock:
			return 'mass repricing: action: discard'
		case OptimizationAction.Reset:
			return 'mass repricing: action: revert'
		default:
			return 'mass repricing: action: start'
	}
}

type Optimizations = {
	optimization_group_id: string
	optimization_status: string
	optimization_id: string
}[]
type ActionButtonsProps = {
	handleClick: (props: HandleClickPropsType) => Promise<void>
	handleExport: (e: React.MouseEvent<HTMLButtonElement>) => void
}

type HandleClickPropsType = {
	action: OptimizationAction
	analyticEvent: string
}
